/**
 * Developer Help hub — the "Cortex Recipe Cards" landing page.
 * Shows the create → powers → connect journey and one card per recipe.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight as ArrowForwardIcon } from 'lucide-react';

import { Button, Chip, ShellHeader } from '@coordio/ui';
import { PageContainer } from '@coordio/ui/layout';

import { getEffectiveApiUrl } from '../config/runtimeEnv';
import { getRecipes } from '../content/developerHelp/recipes';
import { ROUTES } from '../utils/routes';

const JOURNEY = ['0. Builder Map', '1. Create an Agent', '2. Give It Powers', '3. Connect & Call'];

export const DeveloperHelpHubPage = () => {
  const navigate = useNavigate();

  const baseUrl = useMemo(() => {
    const apiUrl = getEffectiveApiUrl();
    if (apiUrl.startsWith('http')) return apiUrl.replace(/\/api\/?$/, '');
    return typeof window !== 'undefined' ? window.location.origin : 'https://your-agentops-host';
  }, []);

  const recipes = useMemo(() => getRecipes(baseUrl), [baseUrl]);

  const openRecipe = (slug: string) => navigate(`${ROUTES.DEVELOPER_HELP}/${slug}`);

  return (
    <div className="developer-help-hub" data-testid="developer-help-detail-page">
      <ShellHeader
        title="Developer Guides"
        description="Short, copy-paste-ready Cortex Recipe Cards that take you from nothing to a working integration."
      />

      <PageContainer>
        <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-3 mb-4" data-testid="developer-help-section-journey">
          <h6 className="text-base font-semibold mb-2">
            The journey
          </h6>
          <div className="flex items-center gap-1 flex-wrap">
            {JOURNEY.map((label, index) => (
              <div key={label} className="flex items-center gap-1">
                <Chip variant="outlined" color={index === 0 ? 'default' : 'accent'} label={label} />
                {index < JOURNEY.length - 1 && <ArrowForwardIcon size={16} className="text-gray-400" />}
              </div>
            ))}
          </div>
          <p className="text-sm text-[var(--color-text-secondary)] mt-2">
            Each recipe lists its ingredients (prerequisites), numbered steps with code you can copy, a
            taste test to verify it worked, and the common burns to avoid.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {recipes.map((recipe) => (
            <div
              key={recipe.slug}
              className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 flex flex-col h-full cursor-pointer hover:border-[var(--color-border-hover)] transition-colors"
              onClick={() => openRecipe(recipe.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openRecipe(recipe.slug); }}
            >
              <div className="flex items-center gap-1 mb-1">
                <Chip variant="outlined" color="accent" label={`Recipe ${recipe.number}`} />
                <Chip variant="outlined" label={recipe.time} />
              </div>
              <h6 className="text-base font-semibold mb-1">
                {recipe.title}
              </h6>
              <p className="text-sm text-[var(--color-text-secondary)] flex-grow">
                {recipe.goal}
              </p>
              <Button size="sm" variant="ghost" className="mt-2 self-start gap-2">
                Open recipe
                <ArrowForwardIcon size={16} />
              </Button>
            </div>
          ))}
        </div>
      </PageContainer>
    </div>
  );
};
